import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useHasLicense } from '../contexts/LicenseContext';
import { useRBAC } from '../hooks/useRBAC';
import Navigation from './Navigation';
import Card from './Card';
import LoadingSpinner from './LoadingSpinner';
import IntelligenceNarrative from './IntelligenceNarrative';
import { severityPostureScore, severityPostureLevel } from '../utils/riskScoringEngine';
import { useLiveDashboard } from '../hooks/useLiveDashboard';
import {
  OverallRiskScoreCard,
  VulnerabilityDistributionCard,
  PredictiveRiskTimelineCard,
  getRiskLevelColor,
} from './shared/DashboardRiskCards';

// Business Unit portfolio has no single org-level assessment (unlike Ransomware/Enterprise/TPRM,
// which each read one org-level row) — every BusinessUnitAssessment is scoped to one application
// within one business unit. The KPI cards below are aggregated org-wide across that whole portfolio
// via the existing /businessUnitRisks endpoints (already built and labeled "the BU dashboard call"),
// not read from a single assessment row.
const BusinessUnitDashboard = () => {
  const apiCall = useApi();
  const hasLicense = useHasLicense();
  const showBusinessUnits = hasLicense('BusinessUnits');
  const showEnterprise = hasLicense('Enterprise');
  const { canManageOrganizationRisks } = useRBAC();

  const [organizationAnalytics, setOrganizationAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const [predictiveRisk, setPredictiveRisk] = useState({ day45: 0, day90: 0, day180: 0 });
  const [loadingPredictive, setLoadingPredictive] = useState(true);
  const [rawPredictive, setRawPredictive] = useState({ day45: 0, day90: 0, day180: 0 });

  // Load all dashboard source data. `silent` skips the loading-state toggles so the live background
  // refresh swaps numbers in place with no spinner/flicker; the mount call passes silent=false for
  // the normal first-load spinners. The independent fetches run in parallel.
  const refreshData = useCallback(async (silent = false) => {
    const org = JSON.parse(localStorage.getItem('selectedOrganization'));
    if (!org?.organizationId) return;

    const fetchOrgAnalytics = async () => {
      if (!silent) setLoading(true);
      try {
        const dist = await apiCall('GET', '/businessUnitRisks/vulnerability-distribution');
        const counts = dist.risk_distribution || {};
        const allRisksCount = dist.all_risks || dist.total_risks || 0;
        const activeRisksCount = dist.total_risks || 0;
        const remediatedCount = allRisksCount - activeRisksCount;
        const score = severityPostureScore(counts);
        const level = severityPostureLevel(score);

        setOrganizationAnalytics({
          overall_risk_score: score,
          overall_RiskLevel: level,
          total_risks: allRisksCount,
          active_risks: activeRisksCount,
          risk_distribution: counts,
          remediated_risks: remediatedCount,
        });
      } catch (err) {
        setOrganizationAnalytics({
          overall_risk_score: 0, overall_RiskLevel: 'LOW', total_risks: 0,
          active_risks: 0, risk_distribution: {}, remediated_risks: 0,
        });
      } finally {
        if (!silent) setLoading(false);
      }
    };

    const fetchPredictiveRisk = async () => {
      if (!silent) setLoadingPredictive(true);
      try {
        const data = await apiCall('GET', '/businessUnitRisks/organization-predictive-risk');
        setRawPredictive({
          day45: data?.predictive45DayScore || 0,
          day90: data?.predictive90DayScore || 0,
          day180: data?.predictive180DayScore || 0,
        });
      } catch (err) {
        setRawPredictive({ day45: 0, day90: 0, day180: 0 });
      } finally {
        if (!silent) setLoadingPredictive(false);
      }
    };

    await Promise.all([fetchOrgAnalytics(), fetchPredictiveRisk()]);
  }, [apiCall]);

  useEffect(() => { refreshData(false); }, [refreshData]);
  const liveRefresh = useCallback(() => refreshData(true), [refreshData]);
  useLiveDashboard({ refresh: liveRefresh });

  useEffect(() => {
    setPredictiveRisk({ day45: rawPredictive.day45, day90: rawPredictive.day90, day180: rawPredictive.day180 });
  }, [rawPredictive]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />

      <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
        <div className="mb-3">
          <h1 className="text-3xl font-bold text-content-primary">
            <span className="text-indigo-700">Business Unit Dashboard</span>
          </h1>
          <p className="mt-2 text-gray-600">
            Organization-wide risk overview across all business units
          </p>
        </div>

        {/* Intelligence Narrative — RISK_MANAGER+ at ORG only */}
        {canManageOrganizationRisks() && (
          <IntelligenceNarrative
            type="bu"
            title="Intelligence Narrative"
            subtitle="AI-generated briefing based on business unit risks"
            accentColor="blue"
          />
        )}

        <div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <OverallRiskScoreCard analytics={organizationAnalytics} title="Business Unit Risk Score" />
            <VulnerabilityDistributionCard analytics={organizationAnalytics} />
            <PredictiveRiskTimelineCard predictiveRisk={predictiveRisk} loading={loadingPredictive} analytics={organizationAnalytics} />
          </div>
        </div>

        {showBusinessUnits && showEnterprise && (
          <div className="mb-4">
            <EnterpriseBUAssessmentCard />
          </div>
        )}

        {showBusinessUnits && (
          <div className="mb-4">
            <TopBusinessUnitsByRiskCard />
          </div>
        )}

        <div className="mb-4">
          <BusinessUnitRemediationTracker />
        </div>
      </div>
    </div>
  );
};

// Enterprise BU Assessment — tile grid of business units with their Enterprise-scoped assessment
// status, mirroring the "Organization Assessment" card other dashboards show (e.g.
// RansomwareOrganizationAssessmentCard). Enterprise-BU assessments have no single org-level row to
// read the way Ransomware's org-level assessment does — every one is scoped to a specific BU — so
// this reuses the existing category=Enterprise summary (the same data EnterpriseBusinessUnits.js
// itself lists) instead of a single-assessment card. Only Enterprise, deliberately — no other
// module's BU-scoped data belongs here.
const EnterpriseBUAssessmentCard = () => {
  const apiCall = useApi();
  const navigate = useNavigate();
  const [businessUnits, setBusinessUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  const getRiskLevelFromScore = (score) => severityPostureLevel(score);
  const calcBURiskScore = (riskLevel) => {
    switch ((riskLevel || '').toUpperCase()) {
      case 'CRITICAL': return 95;
      case 'HIGH': return 80;
      case 'MEDIUM': return 60;
      case 'LOW': return 30;
      default: return 0;
    }
  };

  const fetchBusinessUnits = useCallback(async (silent = false) => {
    try {
      const data = await apiCall('GET', '/businessUnits/summary?category=Enterprise&pageSize=5');
      setBusinessUnits((data.items || []).map(bu => ({
        ...bu,
        riskCounts: {
          CRITICAL: bu.riskCounts?.critical || 0,
          HIGH: bu.riskCounts?.high || 0,
          MEDIUM: bu.riskCounts?.medium || 0,
          LOW: bu.riskCounts?.low || 0,
        },
        totalRisks: (bu.riskCounts?.critical || 0) + (bu.riskCounts?.high || 0) + (bu.riskCounts?.medium || 0) + (bu.riskCounts?.low || 0),
        riskScore: calcBURiskScore(bu.riskLevel),
        hasAssessment: (bu.applicationCount || 0) > 0,
      })));
    } catch {
      // keep prior list on transient failure
    } finally {
      if (!silent) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCall]);

  useEffect(() => { fetchBusinessUnits(false); }, [fetchBusinessUnits]);

  const liveRefreshBUs = useCallback(() => fetchBusinessUnits(true), [fetchBusinessUnits]);
  useLiveDashboard({ refresh: liveRefreshBUs });

  if (!loading && businessUnits.length === 0) return null;

  return (
    <Card>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Enterprise BU Assessment</h3>
        <button onClick={() => navigate('/EnterpriseBusinessUnits')} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
          View All →
        </button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {businessUnits.map((bu) => {
            const score = bu.riskScore || 0;
            const level = getRiskLevelFromScore(score);
            const hasRisk = bu.hasAssessment && bu.totalRisks > 0;
            const badgeClass = getRiskLevelColor(level);
            const barClass = level === 'CRITICAL' ? 'bg-red-700' : level === 'HIGH' ? 'bg-orange-500' : level === 'MEDIUM' ? 'bg-yellow-500' : 'bg-green-500';
            const scoreTextClass = level === 'CRITICAL' ? 'text-red-700' : level === 'HIGH' ? 'text-orange-600' : level === 'MEDIUM' ? 'text-yellow-600' : 'text-green-600';
            return (
              <div
                key={bu.businessUnitId}
                className="relative bg-white border border-gray-200 rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-300"
                onClick={() => navigate(bu.assessmentId ? `/EnterpriseAssessments/${bu.assessmentId}/risks` : '/EnterpriseBusinessUnits')}
              >
                <div className={`h-1 ${hasRisk ? barClass : 'bg-gray-200'}`} />
                <div className="p-4">
                  <div className="flex justify-between items-start gap-2 mb-1.5">
                    <h4 className="font-bold text-gray-900 text-sm leading-snug truncate">{bu.businessUnitName}</h4>
                    {hasRisk && <span className={`shrink-0 text-xs font-bold px-2 py-0.5 rounded-full ${badgeClass}`}>{level}</span>}
                  </div>
                  {bu.domain && <p className="text-xs text-gray-400 mb-2">🌐 {bu.domain}</p>}
                  {hasRisk ? (
                    <div className="mb-2">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-400">Risk Score</span>
                        <span className={`font-bold ${scoreTextClass}`}>{score}%</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${barClass}`} style={{ width: `${score}%` }} />
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-300 italic mb-2">No assessment yet</div>
                  )}
                  {hasRisk && (
                    <div className="flex justify-between text-xs text-gray-400 pt-2 border-t border-gray-100">
                      {bu.riskCounts.CRITICAL > 0 && <span className="text-red-600 font-medium">{bu.riskCounts.CRITICAL} Critical</span>}
                      {bu.riskCounts.HIGH > 0 && <span className="text-orange-500 font-medium">{bu.riskCounts.HIGH} High</span>}
                      {bu.riskCounts.MEDIUM > 0 && <span className="text-yellow-600 font-medium">{bu.riskCounts.MEDIUM} Med</span>}
                      {bu.riskCounts.LOW > 0 && <span className="text-green-600 font-medium">{bu.riskCounts.LOW} Low</span>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

// Top Business Unit Applications by Risk — tile grid of the highest-active-risk business units in
// the org, sorted server-side (sortBy=risk) since the default /businessUnits/summary order is
// alphabetical (built for the management list view, not a "top N" ranking). Clicking a BU expands
// its applications in place below the grid (same pattern as Dashboard.js's own Business Units
// card); clicking an application navigates to that application's BU assessment risk view.
const TopBusinessUnitsByRiskCard = () => {
  const apiCall = useApi();
  const navigate = useNavigate();
  const [businessUnits, setBusinessUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  // In-page application expansion — clicking a BU tile selects it and loads its applications
  // below the grid, rather than navigating away (same pattern as Dashboard.js's own Business
  // Units card).
  const [selectedBU, setSelectedBU] = useState(null);
  const [buApplications, setBuApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);

  const calcBURiskScore = (riskLevel) => {
    switch ((riskLevel || '').toUpperCase()) {
      case 'CRITICAL': return 95;
      case 'HIGH': return 80;
      case 'MEDIUM': return 60;
      case 'LOW': return 30;
      default: return 0;
    }
  };
  const getRiskLevelFromScore = (score) => severityPostureLevel(score);

  const fetchBusinessUnits = useCallback(async (silent = false) => {
    try {
      const data = await apiCall('GET', '/businessUnits/summary?sortBy=risk&pageSize=5');
      setBusinessUnits((data.items || []).map(bu => ({
        ...bu,
        riskCounts: {
          CRITICAL: bu.riskCounts?.critical || 0,
          HIGH: bu.riskCounts?.high || 0,
          MEDIUM: bu.riskCounts?.medium || 0,
          LOW: bu.riskCounts?.low || 0,
        },
        totalRisks: (bu.riskCounts?.critical || 0) + (bu.riskCounts?.high || 0) + (bu.riskCounts?.medium || 0) + (bu.riskCounts?.low || 0),
        riskScore: calcBURiskScore(bu.riskLevel),
        hasAssessment: (bu.applicationCount || 0) > 0,
      })));
    } catch {
      // keep prior list on transient failure
    } finally {
      if (!silent) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCall]);

  useEffect(() => { fetchBusinessUnits(false); }, [fetchBusinessUnits]);

  const liveRefreshBUs = useCallback(() => fetchBusinessUnits(true), [fetchBusinessUnits]);
  useLiveDashboard({ refresh: liveRefreshBUs });

  // Guards against out-of-order responses when tiles are clicked in quick succession — only the
  // most recently requested BU may populate the drill-down.
  const buRequestIdRef = useRef(0);

  const fetchBUApplications = async (businessUnitId) => {
    const requestId = ++buRequestIdRef.current;
    setLoadingApplications(true);
    setBuApplications([]);
    try {
      const summaries = await apiCall('GET', `/businessunitassessments/summary?businessUnitId=${businessUnitId}`);
      if (requestId !== buRequestIdRef.current) return;
      setBuApplications(summaries || []);
    } catch {
      if (requestId === buRequestIdRef.current) setBuApplications([]);
    } finally {
      if (requestId === buRequestIdRef.current) setLoadingApplications(false);
    }
  };

  const handleSelectBU = (bu) => {
    setSelectedBU(bu);
    fetchBUApplications(bu.businessUnitId);
  };

  if (!loading && businessUnits.length === 0) return null;

  return (
    <Card>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Top Business Unit Applications by Risk</h3>
        <button onClick={() => navigate('/businessunits')} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
          View All →
        </button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {businessUnits.map((bu) => {
            const score = bu.riskScore || 0;
            const level = getRiskLevelFromScore(score);
            const hasRisk = bu.hasAssessment && bu.totalRisks > 0;
            const badgeClass = getRiskLevelColor(level);
            const barClass = level === 'CRITICAL' ? 'bg-red-700' : level === 'HIGH' ? 'bg-orange-500' : level === 'MEDIUM' ? 'bg-yellow-500' : 'bg-green-500';
            const scoreTextClass = level === 'CRITICAL' ? 'text-red-700' : level === 'HIGH' ? 'text-orange-600' : level === 'MEDIUM' ? 'text-yellow-600' : 'text-green-600';
            const isSelected = selectedBU?.businessUnitId === bu.businessUnitId;
            return (
              <div
                key={bu.businessUnitId}
                className={`relative bg-white border rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 ${isSelected ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'}`}
                onClick={() => handleSelectBU(bu)}
              >
                <div className={`h-1 ${hasRisk ? barClass : 'bg-gray-200'}`} />
                <div className="p-4">
                  <div className="flex justify-between items-start gap-2 mb-1.5">
                    <h4 className="font-bold text-gray-900 text-sm leading-snug truncate">{bu.businessUnitName}</h4>
                    {hasRisk && <span className={`shrink-0 text-xs font-bold px-2 py-0.5 rounded-full ${badgeClass}`}>{level}</span>}
                  </div>
                  {bu.domain && <p className="text-xs text-gray-400 mb-2">🌐 {bu.domain}</p>}
                  {hasRisk ? (
                    <div className="mb-2">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-400">Risk Score</span>
                        <span className={`font-bold ${scoreTextClass}`}>{score}%</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${barClass}`} style={{ width: `${score}%` }} />
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-300 italic mb-2">No assessment yet</div>
                  )}
                  {hasRisk && (
                    <div className="flex justify-between text-xs text-gray-400 pt-2 border-t border-gray-100">
                      {bu.riskCounts.CRITICAL > 0 && <span className="text-red-600 font-medium">{bu.riskCounts.CRITICAL} Critical</span>}
                      {bu.riskCounts.HIGH > 0 && <span className="text-orange-500 font-medium">{bu.riskCounts.HIGH} High</span>}
                      {bu.riskCounts.MEDIUM > 0 && <span className="text-yellow-600 font-medium">{bu.riskCounts.MEDIUM} Med</span>}
                      {bu.riskCounts.LOW > 0 && <span className="text-green-600 font-medium">{bu.riskCounts.LOW} Low</span>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedBU && (
        <div className="mt-6 pt-4 border-t border-gray-100">
          <h4 className="text-sm font-semibold text-gray-800 mb-3">Applications for {selectedBU.businessUnitName}</h4>
          {loadingApplications ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : buApplications.length === 0 ? (
            <p className="text-sm text-gray-400">No applications found.</p>
          ) : (
            <ul className="space-y-2">
              {buApplications.map((app) => {
                const appRiskScore = app.riskScore || 0;
                const clickable = !!app.hasAssessment && !!app.assessmentID;
                return (
                  <li
                    key={app.businessUnitApplicationId}
                    onClick={() => clickable && navigate(`/businessunitassessment/${app.assessmentID}/risks`)}
                    className={`p-3 border rounded-lg ${clickable ? 'cursor-pointer hover:border-blue-300 hover:shadow-sm' : ''} transition-all`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-sm text-gray-800">{app.applicationName}</div>
                      {!app.hasAssessment && <span className="text-xs text-gray-300 italic shrink-0">No assessment yet</span>}
                    </div>
                    {app.hasAssessment && (
                      <>
                        <div className="text-xs text-gray-500 mb-1">Risk Score: {appRiskScore}%</div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${appRiskScore <= 40 ? 'bg-green-500' : appRiskScore <= 60 ? 'bg-yellow-500' : appRiskScore <= 80 ? 'bg-orange-500' : 'bg-red-500'}`}
                            style={{ width: `${appRiskScore}%` }}
                          />
                        </div>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
};

// Remediation Status Tracker — same shape/behavior as the other module dashboards' tracker, pointed
// at the BusinessUnit remediation summary endpoint.
const BusinessUnitRemediationTracker = () => {
  const apiCall = useApi();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchRemediationSummary = useCallback(async (silent = false) => {
    try {
      const org = JSON.parse(localStorage.getItem('selectedOrganization'));
      if (!org?.organizationId) return;

      const response = await apiCall('GET', '/businessUnitRemediations/summary');
      setSummary(response);
    } catch (error) {
      setSummary({
        totalRemediations: 0, byStatus: [], byPriority: [],
        overdueCount: 0, notStartedCount: 0, inProgressCount: 0,
        openCount: 0, completedCount: 0, top_in_progress: [],
      });
    } finally {
      if (!silent) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCall]);

  useEffect(() => { fetchRemediationSummary(false); }, [fetchRemediationSummary]);

  const liveRefreshRemediation = useCallback(() => fetchRemediationSummary(true), [fetchRemediationSummary]);
  useLiveDashboard({ refresh: liveRefreshRemediation });

  if (loading) return <LoadingSpinner />;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'CRITICAL': return 'text-red-700 bg-red-100';
      case 'HIGH': return 'text-orange-600 bg-orange-100';
      case 'MEDIUM': return 'text-yellow-600 bg-yellow-100';
      case 'LOW': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'text-green-600 bg-green-100';
      case 'In Progress': return 'text-blue-600 bg-blue-100';
      case 'Waiting': return 'text-yellow-600 bg-yellow-100';
      case 'Under Review': return 'text-purple-600 bg-purple-100';
      case 'Deferred': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-content-primary flex items-center">
          <CheckCircle className="h-5 w-5 mr-2 text-blue-600" />
          Remediation Status Tracker
        </h3>
        <button
          onClick={() => navigate('/remediations?types=BusinessUnits')}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          View All →
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{summary.notStartedCount}</div>
          <div className="text-sm text-gray-500">Not Started</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{summary.inProgressCount}</div>
          <div className="text-sm text-gray-500">In Progress</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{summary.completedCount}</div>
          <div className="text-sm text-gray-500">Completed</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">{summary.overdueCount}</div>
          <div className="text-sm text-gray-500">Overdue</div>
        </div>
      </div>

      {summary.top_in_progress && summary.top_in_progress.length > 0 && (
        <div>
          <h4 className="font-medium text-content-primary mb-3">Top In-Progress Tasks</h4>
          <div className="space-y-2">
            {summary.top_in_progress.slice(0, 5).map((remediation) => (
              <div key={remediation.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="font-medium text-content-primary truncate">{remediation.title}</div>
                  <div className="text-xs text-gray-500 truncate mt-1">
                    ⚠️ Related to: {remediation.risk_name || 'Not Linked'}
                  </div>
                  <div className="flex items-center space-x-2 mt-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(remediation.priority)}`}>
                      {remediation.priority}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(remediation.status)}`}>
                      {remediation.status}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-content-primary">
                    {remediation.progress_percentage || 0}%
                  </div>
                  <div className="w-16 bg-gray-200 rounded-full h-2 mt-1">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${remediation.progress_percentage || 0}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

export default BusinessUnitDashboard;
